import { useState, useMemo } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Badge,
  Segmented,
  SegmentedButton,
  Searchbar,
  Sheet,
  Button,
  Dialog,
  DialogButton,
} from 'konsta/react'
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  Search,
  Hash,
  DoorOpen,
  DollarSign,
  Calendar,
  User,
  FileText,
  RefreshCw,
  XCircle,
  Eye,
  MessageSquare,
  Scale,
  ArrowRight,
} from 'lucide-react'
import { formatCurrency, formatRelativeTime, cn } from '@/lib/utils'

// --- Types ---

type DisputeStatus = 'pending' | 'investigating' | 'resolved'
type FilterTab = 'pending' | 'investigating' | 'resolved' | 'all'

interface Dispute {
  id: string
  handId: string
  user: string
  room: string
  reason: string
  status: DisputeStatus
  amount: number
  date: Date
  description?: string
  resolution?: string
}

// --- Mock Data ---

const mockDisputes: Dispute[] = [
  {
    id: 'D001',
    handId: 'H004',
    user: '王大明',
    room: '高額桌 VIP',
    reason: '斷線導致自動棄牌',
    status: 'pending',
    amount: 500000,
    date: new Date(Date.now() - 1000 * 60 * 60 * 2),
    description: '在關鍵手牌中因網路斷線被系統自動棄牌。',
  },
  {
    id: 'D002',
    handId: 'H012',
    user: 'David Chen',
    room: '中級桌 #3',
    reason: '牌局結算錯誤',
    status: 'investigating',
    amount: 200000,
    date: new Date(Date.now() - 1000 * 60 * 60 * 8),
    description: '底池分配金額與顯示不符。',
  },
  {
    id: 'D003',
    handId: 'H008',
    user: 'Sarah Lin',
    room: '新手桌 #1',
    reason: '疑似作弊行為',
    status: 'resolved',
    amount: 0,
    date: new Date(Date.now() - 1000 * 60 * 60 * 72),
    resolution: '經調查未發現異常行為。',
  },
  {
    id: 'D004',
    handId: 'H020',
    user: 'Mike Wang',
    room: '高額桌 #1',
    reason: '底池計算錯誤',
    status: 'resolved',
    amount: 150000,
    date: new Date(Date.now() - 1000 * 60 * 60 * 120),
    resolution: '已確認錯誤並退款 $1,500.00。',
  },
  {
    id: 'D005',
    handId: 'H025',
    user: '小美',
    room: '中級桌 #1',
    reason: '延遲導致超時',
    status: 'pending',
    amount: 80000,
    date: new Date(Date.now() - 1000 * 60 * 60 * 4),
    description: '伺服器延遲造成行動超時，被自動棄牌。',
  },
  {
    id: 'D006',
    handId: 'H030',
    user: 'Alex Kim',
    room: 'PLO 高額桌',
    reason: '軟體錯誤',
    status: 'investigating',
    amount: 300000,
    date: new Date(Date.now() - 1000 * 60 * 60 * 24),
    description: '遊戲客戶端顯示的底池金額與實際結算不符。',
  },
]

// --- Helpers ---

const statusConfig: Record<DisputeStatus, { label: string; color: string; bgColor: string }> = {
  pending: { label: '待處理', color: '#FF9500', bgColor: 'bg-[#FF9500]/10' },
  investigating: { label: '調查中', color: '#007AFF', bgColor: 'bg-[#007AFF]/10' },
  resolved: { label: '已解決', color: '#34C759', bgColor: 'bg-[#34C759]/10' },
}

function StatusBadge({ status }: { status: DisputeStatus }) {
  const config = statusConfig[status]
  const Icon = status === 'pending' ? Clock : status === 'investigating' ? Search : CheckCircle2
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full',
        config.bgColor
      )}
      style={{ color: config.color }}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  )
}

// --- Timeline Event ---

interface TimelineEvent {
  title: string
  time: string
  description?: string
  color: string
}

function TimelineDot({ event, isLast }: { event: TimelineEvent; isLast: boolean }) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-2.5 h-2.5 rounded-full mt-1" style={{ backgroundColor: event.color }} />
        {!isLast && <div className="w-0.5 flex-1 bg-[#E5E5EA] mt-1" />}
      </div>
      <div className={cn(!isLast && 'pb-4')}>
        <p className="text-sm font-medium text-black">{event.title}</p>
        <p className="text-xs text-[#8E8E93] mt-0.5">{event.time}</p>
        {event.description && (
          <p className="text-xs text-[#8E8E93] mt-1">{event.description}</p>
        )}
      </div>
    </div>
  )
}

// --- Detail Sheet ---

function DisputeDetailSheet({
  dispute,
  open,
  onClose,
  onAccept,
  onReject,
}: {
  dispute: Dispute | null
  open: boolean
  onClose: () => void
  onAccept: () => void
  onReject: () => void
}) {
  if (!dispute) return null

  const timelineEvents: TimelineEvent[] = [
    {
      title: '玩家提交爭議',
      time: formatRelativeTime(dispute.date),
      description: `${dispute.user} 提交了關於 ${dispute.reason} 的爭議。`,
      color: '#007AFF',
    },
  ]

  if (dispute.status === 'investigating' || dispute.status === 'resolved') {
    timelineEvents.push({
      title: '管理員開始調查',
      time: '受理後 30 分鐘',
      description: '已調閱手牌記錄及伺服器日誌。',
      color: '#FF9500',
    })
  }

  if (dispute.status === 'resolved') {
    timelineEvents.push({
      title: '爭議已解決',
      time: '調查完成',
      description: dispute.resolution,
      color: '#34C759',
    })
  }

  if (dispute.status === 'pending' || dispute.status === 'investigating') {
    timelineEvents.push({
      title: dispute.status === 'pending' ? '等待處理' : '調查進行中',
      time: '進行中',
      color: dispute.status === 'pending' ? '#8E8E93' : '#FF9500',
    })
  }

  return (
    <Sheet
      opened={open}
      onBackdropClick={onClose}
      className="pb-safe"
      style={{ height: 'auto', maxHeight: '90vh' }}
    >
      <div className="overflow-y-auto" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5EA]">
          <h3 className="text-lg font-bold text-black">爭議詳情</h3>
          <button onClick={onClose} className="text-sm font-medium text-[#007AFF]">
            關閉
          </button>
        </div>

        {/* Dispute Info */}
        <Block className="!mt-2">
          <div className="flex items-center justify-between mb-3">
            <span className="text-base font-bold text-black">{dispute.id}</span>
            <StatusBadge status={dispute.status} />
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">玩家</span>
              <span className="font-medium text-black ml-auto">{dispute.user}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Hash className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">手牌</span>
              <span className="font-medium text-black ml-auto">{dispute.handId}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DoorOpen className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">房間</span>
              <span className="font-medium text-black ml-auto">{dispute.room}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">原因</span>
              <span className="font-medium text-black ml-auto">{dispute.reason}</span>
            </div>
            {dispute.amount > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-[#8E8E93]" />
                <span className="text-[#8E8E93]">涉及金額</span>
                <span className="font-semibold text-[#FF3B30] ml-auto">
                  {formatCurrency(dispute.amount)}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">提交時間</span>
              <span className="font-medium text-black ml-auto">
                {formatRelativeTime(dispute.date)}
              </span>
            </div>
          </div>
        </Block>

        {/* Player Statement */}
        {dispute.description && (
          <>
            <BlockTitle className="flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4" />
              玩家陳述
            </BlockTitle>
            <Block strong inset>
              <p className="text-sm text-black leading-relaxed">{dispute.description}</p>
            </Block>
          </>
        )}

        {/* Evidence */}
        <BlockTitle className="flex items-center gap-1.5">
          <Eye className="w-4 h-4" />
          證據資料
        </BlockTitle>
        <Block strong inset>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">手牌回放記錄</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">伺服器連接日誌</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">交易明細</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
          </div>
        </Block>

        {/* Timeline */}
        <BlockTitle className="flex items-center gap-1.5">
          <Scale className="w-4 h-4" />
          處理進度
        </BlockTitle>
        <Block strong inset>
          <div className="space-y-0">
            {timelineEvents.map((event, i) => (
              <TimelineDot
                key={i}
                event={event}
                isLast={i === timelineEvents.length - 1}
              />
            ))}
          </div>
        </Block>

        {/* Resolution */}
        {dispute.status === 'resolved' && dispute.resolution && (
          <>
            <BlockTitle>處理結果</BlockTitle>
            <Block strong inset>
              <p className="text-sm text-[#34C759] font-medium">{dispute.resolution}</p>
            </Block>
          </>
        )}

        {/* Actions - only for pending/investigating */}
        {dispute.status !== 'resolved' && (
          <Block className="!px-4 !mt-4 !mb-4">
            <div className="flex gap-3">
              <Button
                className="flex-1"
                large
                rounded
                onClick={onAccept}
              >
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  接受爭議
                </span>
              </Button>
              <Button
                className="flex-1"
                large
                rounded
                tonal
                onClick={onReject}
                colors={{ tonalBgIos: 'bg-[#FF3B30]/10', tonalTextIos: 'text-[#FF3B30]' }}
              >
                <span className="flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  駁回爭議
                </span>
              </Button>
            </div>
          </Block>
        )}

        <div className="h-4" />
      </div>
    </Sheet>
  )
}

// --- Main Screen ---

export function AdminDisputesScreen() {
  const [activeTab, setActiveTab] = useState<FilterTab>('pending')
  const [search, setSearch] = useState('')
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogAction, setDialogAction] = useState<'accept' | 'reject'>('accept')

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'pending', label: '待處理' },
    { key: 'investigating', label: '調查中' },
    { key: 'resolved', label: '已解決' },
    { key: 'all', label: '全部' },
  ]

  const filteredDisputes = useMemo(() => {
    let disputes = mockDisputes

    if (activeTab !== 'all') {
      disputes = disputes.filter((d) => d.status === activeTab)
    }

    if (search.trim()) {
      const q = search.toLowerCase()
      disputes = disputes.filter(
        (d) =>
          d.id.toLowerCase().includes(q) ||
          d.handId.toLowerCase().includes(q) ||
          d.user.toLowerCase().includes(q) ||
          d.reason.toLowerCase().includes(q) ||
          d.room.toLowerCase().includes(q)
      )
    }

    return disputes
  }, [activeTab, search])

  const counts = useMemo(() => ({
    pending: mockDisputes.filter((d) => d.status === 'pending').length,
    investigating: mockDisputes.filter((d) => d.status === 'investigating').length,
    resolved: mockDisputes.filter((d) => d.status === 'resolved').length,
    all: mockDisputes.length,
  }), [])

  const handleDisputeClick = (dispute: Dispute) => {
    setSelectedDispute(dispute)
    setSheetOpen(true)
  }

  const closeSheet = () => {
    setSheetOpen(false)
    setSelectedDispute(null)
  }

  const handleAccept = () => {
    setDialogAction('accept')
    setDialogOpen(true)
  }

  const handleReject = () => {
    setDialogAction('reject')
    setDialogOpen(true)
  }

  const handleConfirm = () => {
    setDialogOpen(false)
    closeSheet()
  }

  const getStatusIcon = (status: DisputeStatus) => {
    switch (status) {
      case 'pending':
        return <AlertTriangle className="w-5 h-5 text-[#FF9500]" />
      case 'investigating':
        return <Search className="w-5 h-5 text-[#007AFF]" />
      case 'resolved':
        return <CheckCircle2 className="w-5 h-5 text-[#34C759]" />
    }
  }

  const getStatusBg = (status: DisputeStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-[#FF9500]/10'
      case 'investigating':
        return 'bg-[#007AFF]/10'
      case 'resolved':
        return 'bg-[#34C759]/10'
    }
  }

  return (
    <>
      <Navbar title="爭議處理" />

      <div className="overflow-y-auto pb-8">
        {/* Summary Cards */}
        <Block className="!mt-4 !mb-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#FF9500]">{counts.pending}</p>
              <p className="text-xs text-[#8E8E93] mt-0.5">待處理</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#007AFF]">{counts.investigating}</p>
              <p className="text-xs text-[#8E8E93] mt-0.5">調查中</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#34C759]">{counts.resolved}</p>
              <p className="text-xs text-[#8E8E93] mt-0.5">已解決</p>
            </div>
          </div>
        </Block>

        {/* Segmented Tabs */}
        <Block className="!px-4 !mt-2 !mb-0">
          <Segmented strong rounded>
            {tabs.map((tab) => (
              <SegmentedButton
                key={tab.key}
                active={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                <span className="flex items-center gap-1 text-xs">
                  {tab.label}
                  {tab.key !== 'all' && counts[tab.key] > 0 && (
                    <Badge
                      colors={{
                        bg: tab.key === 'pending'
                          ? 'bg-[#FF9500]'
                          : tab.key === 'investigating'
                            ? 'bg-[#007AFF]'
                            : 'bg-[#34C759]',
                      }}
                      small
                    >
                      {counts[tab.key]}
                    </Badge>
                  )}
                </span>
              </SegmentedButton>
            ))}
          </Segmented>
        </Block>

        {/* Searchbar */}
        <Searchbar
          placeholder="搜尋爭議 ID、手牌、玩家..."
          value={search}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
        />

        {/* Results Count */}
        <BlockTitle>
          共 {filteredDisputes.length} 筆記錄
        </BlockTitle>

        {/* Dispute List */}
        {filteredDisputes.length > 0 ? (
          <List strongIos insetIos>
            {filteredDisputes.map((dispute) => (
              <ListItem
                key={dispute.id}
                link
                chevronIos
                onClick={() => handleDisputeClick(dispute)}
                title={
                  <span className="font-semibold text-black">{dispute.reason}</span>
                }
                subtitle={
                  <span className="flex items-center gap-2 text-[#8E8E93]">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {dispute.user}
                    </span>
                    <span>|</span>
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {dispute.handId}
                    </span>
                  </span>
                }
                after={<StatusBadge status={dispute.status} />}
                media={
                  <div
                    className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center',
                      getStatusBg(dispute.status)
                    )}
                  >
                    {getStatusIcon(dispute.status)}
                  </div>
                }
                text={
                  <span className="flex items-center gap-3 text-[#8E8E93]">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(dispute.date)}
                    </span>
                    {dispute.amount > 0 && (
                      <span className="flex items-center gap-1 text-[#FF3B30]">
                        <DollarSign className="w-3 h-3" />
                        {formatCurrency(dispute.amount)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <DoorOpen className="w-3 h-3" />
                      {dispute.room}
                    </span>
                  </span>
                }
              />
            ))}
          </List>
        ) : (
          <Block className="!mt-8">
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#E5E5EA]/50 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-[#8E8E93]" />
              </div>
              <p className="text-base font-semibold text-[#8E8E93]">沒有爭議記錄</p>
              <p className="text-sm text-[#8E8E93]/60 mt-1">
                {search.trim()
                  ? '找不到符合條件的爭議'
                  : `目前沒有${statusConfig[activeTab as DisputeStatus]?.label || ''}的爭議`}
              </p>
            </div>
          </Block>
        )}
      </div>

      {/* Detail Sheet */}
      <DisputeDetailSheet
        dispute={selectedDispute}
        open={sheetOpen}
        onClose={closeSheet}
        onAccept={handleAccept}
        onReject={handleReject}
      />

      {/* Confirmation Dialog */}
      <Dialog
        opened={dialogOpen}
        onBackdropClick={() => setDialogOpen(false)}
        title={dialogAction === 'accept' ? '確認接受爭議' : '確認駁回爭議'}
        content={
          dialogAction === 'accept'
            ? `確定接受此爭議並退款 ${selectedDispute ? formatCurrency(selectedDispute.amount) : ''} 給 ${selectedDispute?.user}？此操作無法撤銷。`
            : `確定駁回 ${selectedDispute?.user} 的爭議？玩家將收到駁回通知。`
        }
        buttons={
          <>
            <DialogButton onClick={() => setDialogOpen(false)}>取消</DialogButton>
            <DialogButton
              strong
              onClick={handleConfirm}
              className={dialogAction === 'reject' ? 'text-[#FF3B30]' : ''}
            >
              {dialogAction === 'accept' ? '確認退款' : '確認駁回'}
            </DialogButton>
          </>
        }
      />
    </>
  )
}
